import { Link } from 'react-router-dom';
import { IconDropdownArrow, IconNoteOnPaper } from 'assets/DesignSystem/SVGComponents';
import BordFlag from 'components/NudosComponents/BordFlag/BordFlag';
import BordPhoto from 'components/NudosComponents/BordPhoto/BordPhoto';
import BordOneToneIcon from 'components/NudosComponents/BordOneToneIcon/BordOneToneIcon';
import { IProductList, ORDER_SHIPMENT_STATUS } from 'types/orders';
import ShipmentItem from '../ShipmentItem/ShipmentItem';
import ShipmentDetailDrawer from '../ShipmentDetailDrawer/ShipmentDetailDrawer';
import PreparationChecklistDrawer from '../PreparationChecklistDrawer/PreparationChecklistDrawer';
import CheckOrAddCommentsModal from '../../../orders/Components/CheckOrAddCommentsModal/CheckOrAddCommentsModal';
import useOperationsCard from './useOperationsCard';
import { nudosBlue } from 'styles/styles.constants';
import './OperationsCard.scss';

interface OperationsCardProps {
  data: IProductList;
  onCountryClick?: (countryName: string) => void;
}

const OperationsCard = ({ data, onCountryClick }: OperationsCardProps) => {
  const {
    showCommentsModal,
    isShipmentDrawerOpen,
    isPreparationDrawerOpen,
    selectedShipmentId,
    orderNumber,
    companyName,
    countryFlag,
    countryName,
    shipmentsCount,
    showFocusButton,
    isCancelled,
    focusIconColor,
    statusLabel,
    statusDate,
    paymentMethod,
    formattedAmount,
    responsibleUser,
    responsibleName,
    hasMoreShipments,
    hasTooManyShipments,
    visibleShipments,
    cardClassNames,
    orderDetailUrl,
    isFocused,
    isShipmentsExpanded,
    allShipmentsData,
    handleToggleShipments,
    handleFocusOrder,
    handleOpenOrCloseCommentsModal,
    handleShipmentClick,
    handleCloseShipmentDrawer,
    handlePreparationClick,
    handleClosePreparationDrawer
  } = useOperationsCard(data);

  return (
    <div className={cardClassNames}>
      <div className="wrapper">
        <div className="leftSection">
          <div className="orderNumberWrapper">
            <Link to={orderDetailUrl} className="orderNumber">
              Nº{orderNumber}
            </Link>
            {showFocusButton && (
              <button onClick={handleFocusOrder}>
                <BordOneToneIcon
                  variant="gps"
                  standardSize={10}
                  stroke={focusIconColor}
                  fill={isFocused ? nudosBlue : 'transparent'}
                />
              </button>
            )}
          </div>
          <div className="companyInfo">
            <button
              className="countryFlagButton"
              onClick={() => countryName && onCountryClick?.(countryName)}
              aria-label={`Filtrar por país ${countryName}`}
            >
              <BordFlag country={countryFlag} variant="square" customWidth="14px" />
            </button>
            <span className="companyName">{companyName}</span>
          </div>
        </div>
        <div className="rightSection">
          <span className="sla">
            <b>{statusLabel}:</b> {statusDate}
          </span>
          <button
            className="openOrderCommentsBtn"
            onClick={handleOpenOrCloseCommentsModal}
            aria-label="Abrir comentarios de la orden"
          >
            <IconNoteOnPaper />
          </button>
        </div>
      </div>

      <div className="divider" />

      {isCancelled ? (
        <div className="cancelledInfoSection">
          <div className="infoRow">
            <span className="label">Método de pago:</span>
            <span className="value">{paymentMethod}</span>
          </div>
          <div className="infoRow">
            <span className="label">Monto:</span>
            <span className="value">{formattedAmount}</span>
          </div>
          <div className="infoRow">
            <span className="label">Responsable:</span>
            <span className="value responsibleValue">
              <BordPhoto
                url={responsibleUser?.photoProfile}
                variant="circle"
                size="s24"
                avatarProps={{ variant: 'circularUser', standardSize: 24 }}
              />
              {responsibleName}
            </span>
          </div>
        </div>
      ) : (
        <>
          <div className="shipmentSection">
            {visibleShipments.map((shipment, index) => (
              <ShipmentItem
                key={`shipment-${shipment.id}-${index}`}
                receiverName={shipment?.receiverName}
                destinationPlace={shipment?.destinationPlace}
                countryName={shipment?.destinationCountry?.name}
                currentShipmentNumber={index + 1}
                totalShipmentsNumber={shipmentsCount}
                shipmentNumber={index + 1}
                trackingInfo={shipment?.trackingInfo}
                status={data?.statusName as ORDER_SHIPMENT_STATUS}
                onClick={() => handleShipmentClick(shipment?.id, index)}
                onPreparationClick={() => handlePreparationClick(shipment?.id, index)}
              />
            ))}
          </div>

          {hasMoreShipments && !hasTooManyShipments && (
            <div className="expandShipmentsBtnWrapper">
              <button className="expandShipmentsBtn" onClick={handleToggleShipments}>
                <span>
                  {isShipmentsExpanded ? 'Ver menos' : 'Ver más'} ({allShipmentsData.length})
                </span>
                <IconDropdownArrow className={isShipmentsExpanded ? 'rotated' : ''} width={14} height={14} />
              </button>
            </div>
          )}

          {hasTooManyShipments && (
            <div className="tooManyShipmentsMessage">
              <span>La orden tiene <b>({shipmentsCount}) envíos.</b> Ver todos desde la interna.</span>
            </div>
          )}
        </>
      )}

      {showCommentsModal && (
        <CheckOrAddCommentsModal
          closeModal={handleOpenOrCloseCommentsModal}
          orderComments={data?.orderComments || []}
          handleSubmitComments={() => Promise.resolve()}
          loading={false}
        />
      )}

      <ShipmentDetailDrawer
        isOpen={isShipmentDrawerOpen}
        onClose={handleCloseShipmentDrawer}
        orderId={data?.orderId}
        shipmentId={selectedShipmentId || undefined}
      />

      <PreparationChecklistDrawer
        isOpen={isPreparationDrawerOpen}
        onClose={handleClosePreparationDrawer}
        orderId={data?.orderId}
        shipmentId={selectedShipmentId || undefined}
      />
    </div>
  );
};

export default OperationsCard;
